/**
 * Phase 2.15 — Technical Validation Findings (shared types + helpers).
 *
 * These rows are evidence/observations gathered by reviewers (VAPT, OSINT,
 * cloud review, etc.). They do NOT contribute to scoring. A reviewer may
 * promote one into an official Finding (assessment_findings); only at that
 * point does it appear in the Findings tab/section of the report.
 */

export type TvfPromotionStatus =
  | "Evidence Only"
  | "Candidate Finding"
  | "Accepted Finding"
  | "Rejected";

export type TvfSeverity = "Critical" | "High" | "Medium" | "Low" | "Informational";

export type TvfStatus =
  | "Open"
  | "In Review"
  | "Remediated"
  | "Accepted Risk"
  | "False Positive";

export type TvfCategory =
  | "External VAPT"
  | "Internal VAPT"
  | "Web Application"
  | "Cloud Security"
  | "OSINT Exposure"
  | "Source Code Exposure"
  | "Credential Exposure"
  | "Dark Web Exposure"
  | "Other";

export interface TvfRow {
  id: string;
  assessment_id: string;
  finding_title: string;
  category: TvfCategory | string;
  severity: TvfSeverity | string;
  affected_asset: string | null;
  description: string | null;
  business_impact: string | null;
  recommendation: string | null;
  evidence_source: string | null;
  status: TvfStatus | string;
  reviewer_notes: string | null;
  promotion_status: TvfPromotionStatus | string;
  promoted_finding_id: string | null;
  promoted_at: string | null;
  promoted_by: string | null;
  created_at: string;
  updated_at: string;
}

export const TVF_CATEGORIES: TvfCategory[] = [
  "External VAPT",
  "Internal VAPT",
  "Web Application",
  "Cloud Security",
  "OSINT Exposure",
  "Source Code Exposure",
  "Credential Exposure",
  "Dark Web Exposure",
  "Other",
];

export const TVF_SEVERITIES: TvfSeverity[] = [
  "Critical",
  "High",
  "Medium",
  "Low",
  "Informational",
];

export const TVF_STATUSES: TvfStatus[] = [
  "Open",
  "In Review",
  "Remediated",
  "Accepted Risk",
  "False Positive",
];

export const TVF_EVIDENCE_SOURCES = [
  "Partner VAPT Report",
  "Manual Reviewer Entry",
  "OSINT",
  "Interview",
  "Documentation",
  "Other",
];

export const TVF_PROMOTION_STATUSES: TvfPromotionStatus[] = [
  "Evidence Only",
  "Candidate Finding",
  "Accepted Finding",
  "Rejected",
];

export const TVF_DISCLAIMER =
  "Technical Validation Findings are evidence and observations captured to support reviewer judgment. They do not modify questionnaire scoring; only items promoted to official Findings appear in the Findings section.";

/**
 * Default control / domain / risk mapping per category (override-able in UI).
 * Risk + priority defaults are derived from severity in `severityDefaults`.
 */
export interface TvfMappingDefaults {
  control_code: string;
  csv_domain: string;
  nist_function: string;
  framework_pill: string;
}

export interface TvfMappingContext {
  finding_title?: string | null;
  affected_asset?: string | null;
  evidence_source?: string | null;
  description?: string | null;
  recommendation?: string | null;
}

function normalizeCategoryForMapping(category: string, context?: TvfMappingContext): string {
  const explicit = (category || "").trim();
  if (explicit && explicit !== "Other") {
    if (/^external\s+vapt$/i.test(explicit) || /^vapt$/i.test(explicit)) return "External VAPT";
    if (/^internal\s+vapt$/i.test(explicit)) return "Internal VAPT";
    if (/^web\s+application$/i.test(explicit)) return "Web Application";
    return explicit;
  }

  const text = [
    explicit,
    context?.finding_title,
    context?.affected_asset,
    context?.evidence_source,
    context?.description,
    context?.recommendation,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/dark\s*web/.test(text)) return "Dark Web Exposure";
  if (/credential|password|secret|token|api\s*key|leaked|breach/.test(text)) return "Credential Exposure";
  if (/source\s*code|github|gitlab|repository|\brepo\b/.test(text)) return "Source Code Exposure";
  if (/osint|open\s*source\s*intelligence|shodan|censys|public\s*exposure/.test(text)) return "OSINT Exposure";
  if (/cloud|\baws\b|azure|\bgcp\b|s3|bucket|storage\s*account/.test(text)) return "Cloud Security";
  if (/internal\s+vapt|internal\s+network|\blan\b|intranet|\bvpn\b/.test(text)) return "Internal VAPT";
  if (/vapt|penetration|pentest|clickjacking|x-frame|content\s*security\s*policy|\bcsp\b|web\s*application|https?:\/\//.test(text)) {
    return "External VAPT";
  }

  return explicit || "Other";
}

export function defaultsForCategory(category: string, context?: TvfMappingContext): TvfMappingDefaults {
  switch (normalizeCategoryForMapping(category, context)) {
    case "External VAPT":
    case "Web Application":
      return {
        control_code: "DE.CM-08",
        csv_domain: "Infrastructure Security",
        nist_function: "Detect",
        framework_pill: "NIST CSF",
      };
    case "Internal VAPT":
      return {
        control_code: "PR.PS-06",
        csv_domain: "Infrastructure Security",
        nist_function: "Protect",
        framework_pill: "NIST CSF",
      };
    case "Cloud Security":
      return {
        control_code: "PR.DS-01",
        csv_domain: "Infrastructure Security",
        nist_function: "Protect",
        framework_pill: "NIST CSF",
      };
    case "OSINT Exposure":
    case "Source Code Exposure":
    case "Credential Exposure":
      return {
        control_code: "DE.CM-08",
        csv_domain: "Security Operations",
        nist_function: "Detect",
        framework_pill: "NIST CSF",
      };
    case "Dark Web Exposure":
      return {
        control_code: "ID.RA-01",
        csv_domain: "Risk Management",
        nist_function: "Identify",
        framework_pill: "NIST CSF",
      };
    default:
      return {
        control_code: "TVF-01",
        csv_domain: "Technical Validation",
        nist_function: "—",
        framework_pill: "CSV Pro",
      };
  }
}

export interface TvfSeverityDefaults {
  risk_level: string;      // canonical Findings values: HIGH | MODERATE | LOW
  priority_level: string;  // canonical Findings values: Immediate | Quick Win | Strategic
  timeframe: string;       // canonical Findings values
}

/**
 * Maps the TVF severity scale (Critical/High/Medium/Low/Informational) to the
 * canonical Findings dropdown values used everywhere else in the platform.
 * Once promoted, a finding is structurally indistinguishable from any other
 * Finding — same risk/priority/timeframe vocabulary.
 */
export function severityDefaults(severity: string): TvfSeverityDefaults {
  switch (severity) {
    case "Critical":
    case "High":
      return { risk_level: "HIGH", priority_level: "Immediate", timeframe: "30–60 days" };
    case "Medium":
      return { risk_level: "MODERATE", priority_level: "Quick Win", timeframe: "60–90 days" };
    case "Low":
      return { risk_level: "LOW", priority_level: "Strategic", timeframe: "90–180 days" };
    case "Informational":
    default:
      return { risk_level: "LOW", priority_level: "Strategic", timeframe: "90–180 days" };
  }
}

// Canonical Findings dropdown options — must stay in sync with
// src/components/reviewer/FindingsTab.tsx (FindingDetailEditor).
export const FINDING_RISK_LEVELS = ["HIGH", "MODERATE", "LOW"] as const;
export const FINDING_PRIORITY_LEVELS = ["Immediate", "Quick Win", "Strategic"] as const;
export const FINDING_TIMEFRAMES = [
  "30–60 days",
  "60–90 days",
  "90–120 days",
  "90–180 days",
] as const;

/** Summary counts used in the report narrative. */
export interface TvfSummary {
  total: number;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
  promotedToFindings: number;
}

export function summarizeTvf(rows: TvfRow[]): TvfSummary {
  const bySeverity: Record<string, number> = {};
  const byCategory: Record<string, number> = {};
  let promoted = 0;
  for (const r of rows) {
    bySeverity[r.severity] = (bySeverity[r.severity] ?? 0) + 1;
    byCategory[r.category] = (byCategory[r.category] ?? 0) + 1;
    if (r.promotion_status === "Accepted Finding" && r.promoted_finding_id) promoted++;
  }
  return {
    total: rows.length,
    bySeverity,
    byCategory,
    promotedToFindings: promoted,
  };
}

export function buildTvfNarrative(rows: TvfRow[]): string | null {
  if (!rows || rows.length === 0) return null;
  const s = summarizeTvf(rows);
  const sevParts = TVF_SEVERITIES
    .filter((sev) => s.bySeverity[sev])
    .map((sev) => `${s.bySeverity[sev]} ${sev}`)
    .join(", ");
  const catParts = Object.entries(s.byCategory)
    .sort((a, b) => b[1] - a[1])
    .map(([c, n]) => `${n} ${c}`)
    .join("; ");
  const promotedClause =
    s.promotedToFindings > 0
      ? ` ${s.promotedToFindings} ${s.promotedToFindings === 1 ? "item has" : "items have"} been promoted into official Findings.`
      : " No items have been promoted into official Findings.";
  return (
    `A total of ${s.total} technical validation ${s.total === 1 ? "observation" : "observations"} ` +
    `(${sevParts || "no severity assigned"}) were captured across ${Object.keys(s.byCategory).length} ` +
    `${Object.keys(s.byCategory).length === 1 ? "category" : "categories"} (${catParts}).` +
    promotedClause
  );
}
